import React from 'react';
import { TuningPreset } from '../../types/index.js';
import { toPersianDigits } from '../../utils/timeUtils.js';

export interface StringPickerProps {
  /** Notes configured for current preset */
  notes: TuningPreset['notes'];
  /** Explicitly selected string (null means auto-detect) */
  selectedString: number | null;
  /** Currently detected note string index (if auto-detecting) */
  detectedString: number | null;
  /** Whether the current pitch is in tune */
  isInTune: boolean;
  /** Callback when user clicks a string */
  onSelectString: (stringNumber: number | null) => void;
  /** Additional container classes */
  className?: string;
}

export const StringPicker: React.FC<StringPickerProps> = ({
  notes,
  selectedString,
  detectedString,
  isInTune,
  onSelectString,
  className = ''
}) => {
  // Strings ordered from lowest pitch (thickest: 6/7) to highest pitch (thinnest: 1)
  const sortedNotes = [...notes].sort((a, b) => b.stringNumber - a.stringNumber);

  // Active string is explicitly selected string, or detected string in auto-detect mode
  const activeStringNumber = selectedString !== null ? selectedString : detectedString;

  // FRE-139: 4 to 12 strings; denser cells from 8 strings up so 10 fit a 390px phone, tighter again from 11 (12 columns)
  const dense = sortedNotes.length >= 8;
  const veryDense = sortedNotes.length >= 11;

  return (
    <div
      data-testid="tuner-string-picker"
      dir="ltr"
      style={{ gridTemplateColumns: `repeat(${sortedNotes.length}, minmax(0, 1fr))` }}
      className={`grid ${veryDense ? 'gap-0.5' : dense ? 'gap-1' : 'gap-1.5'} md:gap-2 w-full max-w-[640px] ${className}`}
    >
      {sortedNotes.map((note) => {
        const isActive = activeStringNumber === note.stringNumber;

        let activeStyle = 'border-base-300 bg-base-100/80 text-base-content hover:border-base-content/40';
        if (isActive) {
          activeStyle = isInTune
            ? 'border-success bg-success/12 text-success-soft shadow-[0_0_12px_rgba(34,197,94,0.25)]'
            : 'border-error bg-error/12 text-error-soft';
        }

        return (
          <button
            key={note.stringNumber}
            type="button"
            data-testid={`tuner-string-${note.stringNumber}`}
            data-string={note.stringNumber}
            aria-pressed={isActive}
            aria-label={`سیم ${toPersianDigits(note.stringNumber)}: ${note.note}${note.octave}`}
            onClick={() => {
              // Clicking active selected string returns to auto-detect
              if (selectedString === note.stringNumber) {
                onSelectString(null);
              } else {
                onSelectString(note.stringNumber);
              }
            }}
            className={`${veryDense ? 'h-[42px]' : dense ? 'h-[46px]' : 'h-[52px]'} md:h-[64px] border rounded-lg md:rounded-[14px] font-mono flex flex-col items-center justify-center gap-0.5 md:gap-1 transition-all duration-150 select-none ${activeStyle}`}
          >
            <span className={`${veryDense ? 'text-[11px]' : dense ? 'text-[13px]' : 'text-base'} md:text-[18px] font-bold leading-none`}>
              {note.note}
            </span>
            <span className="hidden md:inline text-[10px] text-base-content/60 leading-none">
              {note.stringNumber}
            </span>
            {/* E2E backward compatibility for note+octave assertions */}
            <span className="sr-only">
              {note.note}{note.octave}
            </span>
          </button>
        );
      })}
    </div>
  );
};

StringPicker.displayName = 'StringPicker';
